import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const useTransferQueue = ({ settings, addLog }) => {
  const [transfers, setTransfers] = useState([]);
  const abortControllers = useRef(new Map());

  const checkSlotAvailable = () => true;

  const startTransfer = useCallback((label, path, type = 'action') => {
    const id = uuidv4();
    const controller = new AbortController();
    abortControllers.current.set(id, controller);
    const entry = { id, type, name: label, path, status: 'in-progress', progress: 0, startedAt: Date.now() };
    setTransfers((prev) => [entry, ...prev]);
    return {
      id,
      signal: controller.signal,
      update: (progress) => setTransfers((prev) => prev.map((t) => t.id === id ? { ...t, progress } : t)),
      succeed: () => {
        abortControllers.current.delete(id);
        setTransfers((prev) => prev.map((t) => t.id === id ? { ...t, status: 'completed', progress: 100, completedAt: Date.now() } : t));
      },
      fail: (err) => {
        abortControllers.current.delete(id);
        setTransfers((prev) => prev.map((t) => t.id === id ? { ...t, status: 'error', detail: err } : t));
      },
      remove: () => {
        abortControllers.current.delete(id);
        setTransfers((prev) => prev.filter((t) => t.id !== id));
      }
    };
  }, []);

  const batchTransfer = useCallback((items) => {
    const entries = items.map((item) => ({
      id: uuidv4(),
      type: 'action',
      name: item.name,
      path: item.path,
      status: 'pending',
      progress: 0,
      startedAt: Date.now()
    }));
    setTransfers((prev) => [...entries, ...prev]);
    return entries.map((e) => ({
      id: e.id,
      start: () => setTransfers((prev) => prev.map((t) => t.id === e.id ? { ...t, status: 'in-progress' } : t)),
      succeed: () => setTransfers((prev) => prev.map((t) => t.id === e.id ? { ...t, status: 'completed', progress: 100, completedAt: Date.now() } : t)),
      fail: (err) => setTransfers((prev) => prev.map((t) => t.id === e.id ? { ...t, status: 'error', detail: err } : t))
    }));
  }, []);

  const updateTransfer = useCallback((id, updates) => {
    setTransfers((prev) => prev.map((t) => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const cancelTransfer = useCallback((id) => {
    const controller = abortControllers.current.get(id);
    if (controller) {
      controller.abort();
      abortControllers.current.delete(id);
    }
    setTransfers((prev) => prev.map((t) => t.id === id && t.status === 'in-progress' ? { ...t, status: 'error', detail: 'Cancelled' } : t));
  }, []);

  const cancelAllActive = useCallback(() => {
    abortControllers.current.forEach((controller) => controller.abort());
    abortControllers.current.clear();
    setTransfers((prev) => prev.map((t) =>
      (t.status === 'in-progress' || t.status === 'pending') ? { ...t, status: 'error', detail: 'Cancelled' } : t
    ));
  }, []);

  const clearFinished = () => {
    setTransfers((prev) => prev.filter((t) => t.status === 'in-progress' || t.status === 'pending'));
  };

  return {
    transfers,
    setTransfers,
    startTransfer,
    batchTransfer,
    checkSlotAvailable,
    updateTransfer,
    cancelTransfer,
    cancelAllActive,
    clearFinished
  };
};
